export type MasterLabelChromeSizing = {
  chromeScale: number;
  toolbarPaddingY: number;
  toolbarVerticalGap: number;
  toolbarControlPaddingY: number;
  toolbarInputPaddingY: number;
  toolbarButtonPaddingY: number;
  toolbarChipPaddingY: number;
  contentPaddingY: number;
  contentGap: number;
  columnHeaderPaddingY: number;
  columnHeaderGap: number;
  headerPrimaryRowGap: number;
  headerActionInset: number;
  warningMarginTop: number;
  warningPaddingY: number;
  warningGap: number;
  sectionHeaderPaddingY: number;
  sectionStackGap: number;
  sectionScrollPaddingY: number;
  sectionBandPaddingY: number;
  sectionBandMarginBottom: number;
  barcodeSectionRowsPaddingTop: number;
  barcodeSectionRowsPaddingBottom: number;
  barcodeRowShellPaddingTopWithImages: number;
  barcodeRowShellPaddingBottomWithImages: number;
  barcodeRowShellPaddingYWithoutImages: number;
  barcodeRowCardPaddingYWithImages: number;
  barcodeRowCardPaddingYWithoutImages: number;
  barcodeRowContentGapWithImages: number;
  barcodeRowContentGapWithoutImages: number;
  barcodeRowTextGapWithImages: number;
  barcodeRowTextGapWithoutImages: number;
  designShellPadding: number;
  designShellMarginBottom: number;
  designHeaderPaddingY: number;
  designHeaderGap: number;
  designCropStripGap: number;
  designCropStripPaddingTop: number;
  designCropStripPaddingBottom: number;
  actionBarPaddingY: number;
  actionChipPaddingY: number;
  saveButtonPaddingY: number;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function scaleChromeSpace(
  base: number,
  chromeScale: number,
  options: { min?: number; max?: number } = {}
): number {
  const min = options.min ?? 0;
  const max = options.max ?? Number.POSITIVE_INFINITY;
  return clamp(Math.round(base * chromeScale), min, max);
}

export function resolveMasterLabelChromeScale(textScale = 100): number {
  return clamp(textScale / 100, 0.6, 1.25);
}

export function resolveMasterLabelChromeSizing(textScale = 100): MasterLabelChromeSizing {
  const chromeScale = resolveMasterLabelChromeScale(textScale);

  return {
    chromeScale,
    toolbarPaddingY: scaleChromeSpace(4, chromeScale, { min: 2 }),
    toolbarVerticalGap: scaleChromeSpace(3, chromeScale, { min: 2 }),
    toolbarControlPaddingY: scaleChromeSpace(3, chromeScale, { min: 2 }),
    toolbarInputPaddingY: scaleChromeSpace(2, chromeScale, { min: 1 }),
    toolbarButtonPaddingY: scaleChromeSpace(2, chromeScale, { min: 1 }),
    toolbarChipPaddingY: scaleChromeSpace(2, chromeScale, { min: 1 }),
    contentPaddingY: scaleChromeSpace(4, chromeScale, { min: 2 }),
    contentGap: scaleChromeSpace(6, chromeScale, { min: 4 }),
    columnHeaderPaddingY: scaleChromeSpace(2, chromeScale, { min: 1 }),
    columnHeaderGap: scaleChromeSpace(4, chromeScale, { min: 2 }),
    headerPrimaryRowGap: scaleChromeSpace(4, chromeScale, { min: 2 }),
    headerActionInset: scaleChromeSpace(2, chromeScale, { min: 1 }),
    warningMarginTop: scaleChromeSpace(6, chromeScale, { min: 3 }),
    warningPaddingY: scaleChromeSpace(6, chromeScale, { min: 3 }),
    warningGap: scaleChromeSpace(4, chromeScale, { min: 2 }),
    sectionHeaderPaddingY: scaleChromeSpace(1, chromeScale, { min: 1 }),
    sectionStackGap: scaleChromeSpace(1, chromeScale, { min: 1 }),
    sectionScrollPaddingY: scaleChromeSpace(1, chromeScale, { min: 1 }),
    sectionBandPaddingY: scaleChromeSpace(1, chromeScale, { min: 1 }),
    sectionBandMarginBottom: scaleChromeSpace(1, chromeScale, { min: 1 }),
    barcodeSectionRowsPaddingTop: scaleChromeSpace(1, chromeScale, { min: 1 }),
    barcodeSectionRowsPaddingBottom: scaleChromeSpace(4, chromeScale, { min: 2 }),
    barcodeRowShellPaddingTopWithImages: scaleChromeSpace(4, chromeScale, { min: 2 }),
    barcodeRowShellPaddingBottomWithImages: scaleChromeSpace(5, chromeScale, { min: 2 }),
    barcodeRowShellPaddingYWithoutImages: scaleChromeSpace(1, chromeScale, { min: 1 }),
    barcodeRowCardPaddingYWithImages: scaleChromeSpace(3, chromeScale, { min: 2 }),
    barcodeRowCardPaddingYWithoutImages: scaleChromeSpace(2, chromeScale, { min: 1 }),
    barcodeRowContentGapWithImages: scaleChromeSpace(2, chromeScale, { min: 1 }),
    barcodeRowContentGapWithoutImages: scaleChromeSpace(1, chromeScale, { min: 1 }),
    barcodeRowTextGapWithImages: scaleChromeSpace(3, chromeScale, { min: 2 }),
    barcodeRowTextGapWithoutImages: scaleChromeSpace(2, chromeScale, { min: 1 }),
    designShellPadding: scaleChromeSpace(4, chromeScale, { min: 2 }),
    designShellMarginBottom: scaleChromeSpace(4, chromeScale, { min: 2 }),
    designHeaderPaddingY: scaleChromeSpace(6, chromeScale, { min: 3 }),
    designHeaderGap: scaleChromeSpace(6, chromeScale, { min: 4 }),
    designCropStripGap: scaleChromeSpace(6, chromeScale, { min: 3 }),
    designCropStripPaddingTop: scaleChromeSpace(4, chromeScale, { min: 2 }),
    designCropStripPaddingBottom: scaleChromeSpace(2, chromeScale, { min: 1 }),
    actionBarPaddingY: scaleChromeSpace(2, chromeScale, { min: 1 }),
    actionChipPaddingY: scaleChromeSpace(3, chromeScale, { min: 2 }),
    saveButtonPaddingY: scaleChromeSpace(4, chromeScale, { min: 2 }),
  };
}
